/** Line drawings for the landing's reach section: a phone, a clicker, a printed card, a chat.
 *
 * Drawn here, not fetched: nothing loads from outside the bundle. Every stroke and fill is a
 * token (`--ink`, `--surface`, `--accent`, `--line`), so the drawings follow paper, the board and
 * the dark theme with no second set. They are decorative: the card's heading says what each
 * one shows, so every <svg> is aria-hidden and carries no text a screen reader would need. The
 * letters on the card are the answer positions, the same A to D the student sees.
 */
import type { ReactNode } from "react";

const ink = "stroke-[color:var(--ink)]";
const paper = "fill-[color:var(--surface)]";
const mark = "fill-[color:var(--accent)]";
const soft = "fill-[color:var(--surface-2)]";

function Frame({ children }: { children: ReactNode }) {
  return (
    <svg aria-hidden focusable="false" viewBox="0 0 120 96" className="h-24 w-auto" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

/** Two phones in the browser, one with the check on it, and the hotspot they joined. */
export function PhonesArt() {
  return (
    <Frame>
      <rect x="16" y="12" width="40" height="76" rx="7" className={`${paper} ${ink}`} />
      <rect x="24" y="26" width="24" height="7" rx="2" className={soft} />
      <rect x="24" y="37" width="24" height="7" rx="2" className={mark} />
      <rect x="24" y="48" width="24" height="7" rx="2" className={soft} />
      <rect x="24" y="59" width="24" height="7" rx="2" className={soft} />
      <rect x="62" y="22" width="40" height="70" rx="7" className={`${paper} ${ink}`} />
      <path d="M72 56l7 7 14-15" className={ink} strokeWidth="3" />
      <path d="M96 13a12 12 0 0 1 14 0M99 17a7 7 0 0 1 8 0" className={ink} />
      <circle cx="103" cy="21" r="1.6" className={`${mark} ${ink}`} />
    </Frame>
  );
}

/** The two-button clicker and the hub it talks to; no screen, nothing to set up. */
export function ClickerArt() {
  return (
    <Frame>
      <rect x="22" y="14" width="44" height="72" rx="20" className={`${paper} ${ink}`} />
      <circle cx="44" cy="38" r="10" className={`${mark} ${ink}`} />
      <path d="M39 38l4 4 6-7" className="stroke-[color:var(--accent-ink)]" strokeWidth="2.5" />
      <circle cx="44" cy="63" r="10" className={`${soft} ${ink}`} />
      <path d="M40 59l8 8M48 59l-8 8" className={ink} />
      <path d="M74 34a10 10 0 0 1 0 14M79 29a17 17 0 0 1 0 24" className={ink} />
      <rect x="90" y="30" width="16" height="26" rx="3" className={`${paper} ${ink}`} />
      <path d="M94 56v8h8v-8" className={ink} />
    </Frame>
  );
}

/** A printed card held up, its marker turned to one answer, inside a phone camera's frame. */
export function CardArt() {
  return (
    <Frame>
      <path d="M14 22v-8h8M98 14h8v8M106 74v8h-8M22 82h-8v-8" className={ink} />
      <rect x="30" y="18" width="60" height="60" rx="4" className={`${paper} ${ink}`} />
      <rect x="44" y="32" width="32" height="32" className="fill-[color:var(--ink)]" />
      <rect x="54" y="32" width="12" height="11" className={mark} />
      <g className="fill-[color:var(--ink-2)]" fontSize="9" fontWeight="700" textAnchor="middle">
        <text x="60" y="28">A</text>
        <text x="85" y="51">B</text>
        <text x="60" y="75">C</text>
        <text x="35" y="51">D</text>
      </g>
    </Frame>
  );
}

/** A question arriving in a chat, with its answer buttons under it. */
export function ChatArt() {
  return (
    <Frame>
      <path d="M14 18a8 8 0 0 1 8-8h68a8 8 0 0 1 8 8v22a8 8 0 0 1-8 8H30l-10 8v-8.6A8 8 0 0 1 14 40Z" className={`${paper} ${ink}`} />
      <path d="M26 24h52M26 34h36" className={ink} />
      <rect x="14" y="62" width="40" height="14" rx="5" className={`${mark} ${ink}`} />
      <rect x="58" y="62" width="40" height="14" rx="5" className={`${paper} ${ink}`} />
      <rect x="14" y="80" width="40" height="14" rx="5" className={`${paper} ${ink}`} />
      <rect x="58" y="80" width="40" height="14" rx="5" className={`${paper} ${ink}`} />
    </Frame>
  );
}
